// /Users/pramod/Desktop/school-erp/frontend/src/Tenant/TenantWebsite/sections/Statistics.js

import React, { useEffect, useRef, useState } from 'react';
import { 
  FiTrendingUp, FiUsers, FiBriefcase, FiAward, FiGlobe, FiStar, 
  FiBookOpen, FiClock, FiMapPin, FiHeart, FiSmile, FiThumbsUp,
  FiBarChart2, FiUserCheck, FiTarget, FiZap
} from 'react-icons/fi';
import useBrandColors from '../utils/useBrandColors';

const iconMap = {
  'trending-up': FiTrendingUp,
  'users': FiUsers,
  'briefcase': FiBriefcase,
  'award': FiAward,
  'globe': FiGlobe,
  'star': FiStar,
  'chart-bar': FiBarChart2,
  'user-check': FiUserCheck,
  'book': FiBookOpen,
  'clock': FiClock,
  'map-pin': FiMapPin,
  'heart': FiHeart,
  'smile': FiSmile,
  'thumbs-up': FiThumbsUp,
  'target': FiTarget,
  'zap': FiZap,
  'book-open': FiBookOpen,
  'users-2': FiUsers,
  'trending': FiTrendingUp,
};

const StatItem = ({ stat, animate, index }) => {
  const [count, setCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const ref = useRef(null);
  const Icon = iconMap[stat.icon] || FiTrendingUp;
  const { primary, secondary, fontBody, fontHeading } = useBrandColors();

  useEffect(() => {
    const currentRef = ref.current;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  useEffect(() => {
    if (!isVisible || !animate) return;

    let start = 0;
    const end = stat.value || 0;
    const duration = 2000 + index * 100; // Stagger animation
    const increment = end / (duration / 16);

    const timer = setInterval(() => {
      start += increment;
      if (start >= end) {
        setCount(end);
        clearInterval(timer);
      } else {
        setCount(Math.floor(start));
      }
    }, 16);

    return () => clearInterval(timer);
  }, [isVisible, animate, stat.value, index]);

  const displayValue = animate && isVisible ? count : stat.value;

  return (
    <div 
      ref={ref}
      className="group relative bg-white rounded-2xl p-8 text-center transition-all duration-500 hover:shadow-2xl hover:-translate-y-2 border border-gray-100"
      style={{ 
        fontFamily: fontBody,
        boxShadow: isHovered ? `0 20px 60px ${primary}15` : '0 4px 20px rgba(0,0,0,0.04)',
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Gradient Background on Hover */}
      <div 
        className="absolute inset-0 rounded-2xl transition-opacity duration-700"
        style={{ 
          background: `linear-gradient(135deg, ${primary}08, ${secondary}08)`,
          opacity: isHovered ? 1 : 0,
        }}
      />
      
      {/* Decorative Circle */}
      <div 
        className="absolute -top-6 -right-6 w-24 h-24 rounded-full transition-all duration-700"
        style={{ 
          background: `radial-gradient(circle, ${primary}10, transparent 70%)`,
          transform: isHovered ? 'scale(1.2)' : 'scale(1)',
          opacity: isHovered ? 1 : 0,
        }}
      />

      <div className="relative z-10">
        {/* Icon with animated background */}
        <div 
          className="inline-flex items-center justify-center w-20 h-20 rounded-2xl mb-5 transition-all duration-500"
          style={{ 
            background: isHovered 
              ? `linear-gradient(135deg, ${primary}, ${secondary})` 
              : `linear-gradient(135deg, ${primary}15, ${secondary}15)`,
            transform: isHovered ? 'scale(1.05) rotate(-3deg)' : 'scale(1) rotate(0deg)',
            boxShadow: isHovered ? `0 10px 30px ${primary}30` : 'none',
          }}
        >
          <Icon 
            className={`w-9 h-9 transition-all duration-500`}
            style={{ 
              color: isHovered ? '#ffffff' : secondary,
              transform: isHovered ? 'scale(1.1)' : 'scale(1)',
            }}
          />
        </div>
        
        {/* Counter with prefix/suffix */}
        <div 
          className="text-4xl md:text-5xl font-bold mb-2 tracking-tight"
          style={{ 
            color: primary,
            fontFamily: fontHeading,
            letterSpacing: '-0.02em',
          }}
        >
          {stat.prefix}
          <span className="relative">
            {displayValue}
            {/* Optional underline animation */}
            <span 
              className="absolute -bottom-1 left-0 h-0.5 transition-all duration-500"
              style={{ 
                width: isHovered ? '100%' : '0%',
                background: `linear-gradient(90deg, ${primary}, ${secondary})`,
              }}
            />
          </span>
          {stat.suffix}
        </div>
        
        {/* Label */}
        <p 
          className="text-sm font-medium transition-colors duration-300"
          style={{ 
            color: isHovered ? primary : '#6B7280',
          }}
        >
          {stat.label}
        </p>

        {/* Small decorative dot */}
        <div 
          className="mt-4 mx-auto w-2 h-2 rounded-full transition-all duration-500"
          style={{ 
            background: `linear-gradient(90deg, ${primary}, ${secondary})`,
            opacity: isHovered ? 1 : 0.3,
            transform: isHovered ? 'scale(1.5)' : 'scale(1)',
          }}
        />
      </div>
    </div>
  );
};

const Statistics = ({ statistics }) => {
  const { 
    sectionTitle, 
    sectionSubtitle, 
    displayStyle = 'grid-4',
    animateCounters = true, 
    items = [],
    backgroundColor = 'gray-50',
    textColor = 'gray-900',
  } = statistics || {};
  
  const { primary, secondary, fontBody, fontHeading } = useBrandColors();

  if (!items || items.length === 0) return null;

  const gridClasses = {
    'grid-4': 'grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 lg:gap-8',
    'grid-3': 'grid-cols-1 md:grid-cols-3 gap-6 md:gap-8',
    'grid-2': 'grid-cols-1 md:grid-cols-2 gap-6 md:gap-8',
    'carousel': 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6',
    'featured': 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8',
  };

  // Color background mapping
  const bgColors = {
    'white': 'bg-white',
    'gray-50': 'bg-gray-50/50',
    'gray-100': 'bg-gray-100/50',
    'primary': `bg-[${primary}]`,
    'primary-light': `bg-[${primary}05]`,
    'transparent': 'bg-transparent',
  };

  const bgClass = bgColors[backgroundColor] || 'bg-gray-50/50';

  // Gradient overlay for section
  const gradientStyle = {
    background: `linear-gradient(135deg, ${primary}03, ${secondary}03)`,
  };

  return (
    <section id="statistics" className={`py-16 md:py-24 ${bgClass} relative overflow-hidden`}>
      {/* Decorative background elements */}
      <div 
        className="absolute inset-0 pointer-events-none"
        style={gradientStyle}
      />
      
      {/* Decorative circle elements */}
      <div 
        className="absolute top-0 right-0 w-96 h-96 rounded-full blur-3xl opacity-10"
        style={{ background: primary }}
      />
      <div 
        className="absolute bottom-0 left-0 w-96 h-96 rounded-full blur-3xl opacity-10"
        style={{ background: secondary }}
      />

      <div className="container mx-auto px-6 lg:px-12 relative">
        <div className="max-w-7xl mx-auto">
          {/* Header Section - Enhanced */}
          {(sectionTitle || sectionSubtitle) && (
            <div className="text-center mb-12 md:mb-16 lg:mb-20">
              {/* Subtitle badge */}
              <span 
                className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-medium tracking-[0.15em] uppercase mb-4"
                style={{ 
                  color: secondary,
                  backgroundColor: `${secondary}15`,
                  fontFamily: fontBody,
                }}
              >
                <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: secondary }} />
                Statistics
              </span>
              
              {sectionTitle && (
                <h2 
                  className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4"
                  style={{ 
                    color: primary,
                    fontFamily: fontHeading,
                    letterSpacing: '-0.02em',
                  }}
                >
                  {sectionTitle}
                </h2>
              )}
              
              {sectionSubtitle && (
                <p 
                  className="text-lg max-w-2xl mx-auto font-light"
                  style={{ 
                    color: textColor === 'gray-900' ? '#6B7280' : '#9CA3AF',
                    fontFamily: fontBody,
                  }}
                >
                  {sectionSubtitle}
                </p>
              )}

              {/* Decorative line */}
              <div className="mt-6 flex justify-center">
                <div className="h-1 w-20 rounded-full" style={{ 
                  background: `linear-gradient(90deg, ${primary}, ${secondary})` 
                }} />
              </div>
            </div>
          )}

          {/* Statistics Grid */}
          <div className={gridClasses[displayStyle] || gridClasses['grid-4']}>
            {items.map((stat, index) => (
              <StatItem 
                key={stat._id || index} 
                stat={stat} 
                animate={animateCounters} 
                index={index}
              />
            ))}
          </div>

          {/* Optional: Trust Badge */}
          {items.length > 3 && (
            <div className="mt-12 text-center">
              <p 
                className="text-sm font-light flex items-center justify-center gap-2"
                style={{ color: '#9CA3AF' }}
              >
                <span className="w-1 h-1 rounded-full" style={{ backgroundColor: secondary }} />
                Trusted by thousands of students and parents
                <span className="w-1 h-1 rounded-full" style={{ backgroundColor: secondary }} />
              </p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default Statistics;